import { useCallback } from 'react';
import { countriesApi } from '../api/countries.api';
import { COUNTRY_HIERARCHY_INPUT_KEY } from '../hooks/queryKeys';
import type { Country } from '../types/countries.types';
import { TerritorialLevel } from '../types/countries.types';
import { HierarchyInput } from '@/shared/components/ahkinia/HierarchyInput';
import type {
  HierarchyFetchParams,
  HierarchyInputProps,
} from '@/shared/components/ahkinia/HierarchyInput';

type CountryHierarchyInputProps = Omit<
  HierarchyInputProps<Country>,
  'fetchItems' | 'fetchItemById' | 'getLabel' | 'getParent' | 'queryKey'
>;

export function CountryHierarchyInput(props: CountryHierarchyInputProps) {
  const fetchItems = useCallback(async ({ parentId, pageSize = 100 }: HierarchyFetchParams) => {
    if (!parentId) {
      const result = await countriesApi.getCountries({
        level: TerritorialLevel.Pais,
        isActive: true,
        pageSize,
      });
      return { items: result.data?.items ?? [], hasMore: result.data?.hasNextPage ?? false };
    }
    const hierarchy = await countriesApi.getCountryHierarchy(parentId);
    return { items: (hierarchy.data?.children ?? []).filter((c) => c.isActive) as Country[] };
  }, []);

  const fetchItemById = useCallback(async (id: string) => {
    const result = await countriesApi.getCountryById(id);
    return result.data as Country;
  }, []);

  return (
    <HierarchyInput<Country>
      {...props}
      fetchItems={fetchItems}
      fetchItemById={fetchItemById}
      getLabel={(c) => c.commonName}
      getParent={(c) => c.parentId}
      queryKey={COUNTRY_HIERARCHY_INPUT_KEY}
    />
  );
}
